import { Vector2, Vector3 } from 'three';
import { radToDeg } from 'three/src/math/MathUtils.js';
import { Color, Element, LineElement, NoneElement } from '@thive/canvas';
import { InterAreaConnection } from '@thive/fddm-types';

import { theme } from '@/modules/common/theme';
import { createBubbleElement } from './bubble';
import { FDDM_TAG, INTERACTIVITY_OFF } from '@/modules/floorplanDesign/constants';
import { convertV2ToV3, getVectorRotatedAroundPoint } from '@/modules/common/helpers/math';
import { calcBubbleElementScales } from '@/modules/canvas/helpers';
import { Z_INDEX_MAP } from '@/modules/canvas/constants/elements';
import { angleBetweenVectors } from '@/modules/floorplanDesign/helpers/helpers';

const midpoint = (a: Vector2, b: Vector2): Vector2 => new Vector2((a.x + b.x) / 2, (a.y + b.y) / 2);

export const createDistantConnectionElement = (
  id: string,
  fromPosition: Vector2,
  toPosition: Vector2,
  zoom: number,
  interAreaConnection: InterAreaConnection,
  visible = true,
): Element[] => {
  const elements: Element[] = [];

  const bubblePosition = midpoint(fromPosition, toPosition);
  const angle =
    radToDeg(angleBetweenVectors(toPosition.clone().sub(fromPosition), new Vector2(1, 0))) + 90;

  const group: NoneElement = {
    id,
    type: 'None',
    name: id,
    position: new Vector3(bubblePosition.x, bubblePosition.y, Z_INDEX_MAP[FDDM_TAG.BUBBLE]),
    rotation: new Vector3(0, 0, angle),
    interactivity: INTERACTIVITY_OFF,
    tags: [FDDM_TAG.LAYER_CONNECTIONS],
  };

  elements.push(group);

  const pointA = convertV2ToV3(
    getVectorRotatedAroundPoint(
      new Vector2(fromPosition.x, fromPosition.y),
      new Vector2(bubblePosition.x, bubblePosition.y),
      -angle,
    ).sub(new Vector2(bubblePosition.x, bubblePosition.y)),
    Z_INDEX_MAP[FDDM_TAG.DISTANT_CONNECTION],
  );

  const pointB = convertV2ToV3(
    getVectorRotatedAroundPoint(
      new Vector2(toPosition.x, toPosition.y),
      new Vector2(bubblePosition.x, bubblePosition.y),
      -angle,
    ).sub(new Vector2(bubblePosition.x, bubblePosition.y)),
    Z_INDEX_MAP[FDDM_TAG.DISTANT_CONNECTION],
  );

  const { dividerStrokeWidth: strokeWidth } = calcBubbleElementScales(zoom);

  const line: LineElement = {
    type: 'Line',
    name: `${id}-line`,
    id: `${id}-line`,
    pointA,
    pointB,
    strokeWidth,
    stroke: Color.fromHex(theme.palette.primary.main),
    interactivity: {
      ...INTERACTIVITY_OFF,
      clickable: true,
    },
    tags: [FDDM_TAG.DISTANT_CONNECTION, FDDM_TAG.LAYER_CONNECTIONS],
    parentId: group.id,
    visible,
  };

  elements.push(line);
  elements.push(...createBubbleElement(id, group.id, zoom, interAreaConnection, [], visible));

  return elements;
};
